import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation, Outlet, useSearchParams } from 'react-router-dom';
import LoaderIcon from '@/images/icons/Loader';
import { useHasPermission } from '@/hooks/useHasPermission';
import { getPrevUrl, getProject } from '@/selectors';

export function PermissionRoute({ requiredPermission }) {
	const auth = useSelector((state) => state.auth);
	const project = useSelector(getProject);
	const prevUrl = useSelector(getPrevUrl);
	const currentProjectId = useSelector((state) => state.project.currentId);
	const userPermissions = useSelector((state) => {
		try {
			return state.permissions.byUserId[state.auth.user?.userId]?.byProjectId[state.project.currentId];
		} catch (e) {
			return undefined;
		}
	});
	const permissionsLoading = useSelector((state) => {
		try {
			return state.permissions.byUserId[state.auth.user?.userId]?.isLoading;
		} catch (e) {
			return false;
		}
	});
	
	const location = useLocation();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const hasPermission = useHasPermission(requiredPermission);

	const [timeLeft, setTimeLeft] = useState(3);

	// Check if we're in the middle of project switching
	const isProjectSwitching = searchParams.get("projectId") || 
		(currentProjectId && !project) || 
		(currentProjectId && userPermissions === undefined && permissionsLoading);

	useEffect(() => {
		if (!auth?.user) {
			navigate('/login');
		}
	}, [auth, navigate]);

	useEffect(() => {
		if (auth?.user && !hasPermission && !isProjectSwitching) {
			const interval = setInterval(() => {
				setTimeLeft((prev) => prev - 1);
			}, 1000);

			return () => clearInterval(interval);
		}
	}, [auth, hasPermission, isProjectSwitching]);

	useEffect(() => {
		// Attempt to go back if previous url exists, otherwise go home
		if (timeLeft <= 0 && auth?.user && !hasPermission && !isProjectSwitching) {
			if (prevUrl && (location.pathname !== prevUrl) && prevUrl !== '/logout') {
				navigate(prevUrl);
			} else {
				navigate('/');
			}
		}
	}, [timeLeft, hasPermission, auth, navigate, isProjectSwitching]);

	if (!auth?.user) {
		return null;
	}

	// Show loader if project is switching
	if (isProjectSwitching) {
		return (
			<div className="h-screen flex items-center justify-center">
				<svg className="animate-rotation w-12 h-12 text-[#0D4E58] m-auto"
					 width="24"
					 height="25"
					 viewBox="0 0 24 25"
					 fill="none"
					 xmlns="http://www.w3.org/2000/svg">
					<path d="M22 12.5C22 18.0228 17.5228 22.5 12 22.5C6.47715 22.5 2 18.0228 2 12.5C2 6.97715 6.47715 2.5 12 2.5"
						  stroke="#EAE6E0"
						  strokeWidth="4"
						  strokeLinecap="round" />
					<path d="M12 2.5C11.4467 2.5 10.9039 2.54494 10.375 2.63136"
						  stroke="#EAE6E0"
						  strokeWidth="4"
						  strokeLinecap="round" />
					<path d="M2 12.5C2 18.0228 6.47715 22.5 12 22.5C17.5228 22.5 22 18.0228 22 12.5C22 6.97715 17.5228 2.5 12 2.5"
						  stroke="currentColor"
						  strokeWidth="4"
						  strokeLinecap="round" />
				</svg>
			</div>
		);
	}

	if (!hasPermission) {
		return (
			<div className="p-4">
				<div className="text-center mt-2 md:mt-[64px] w-full flex flex-col bg-brand-beige p-4 rounded-[24px] mb-20 md:max-w-[1090px] md:mx-auto">
					<div className="px-1.5">
						<h1 className="text-4xl md:text-[3rem] leading-none font-founders mt-8 mb-[43px]">
							You don't have access to this page
						</h1>
					</div>

					<div className="mt-5 mb-[90px]">
						<svg className="w-36 h-36 mx-auto" width="144" height="144" viewBox="0 0 144 144" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M103.25 52.3457L103.957 51.6386L103.25 50.9315L93.0674 40.7491L92.3603 40.042L91.6531 40.7491L71.9994 60.4029L52.3457 40.7491L51.6386 40.042L50.9315 40.7491L40.7491 50.9315L40.042 51.6386L40.7491 52.3457L60.4029 71.9994L40.7491 91.6531L40.042 92.3603L40.7491 93.0674L50.9315 103.25L51.6386 103.957L52.3457 103.25L71.9994 83.596L91.6531 103.25L92.3603 103.957L93.0674 103.25L103.25 93.0674L103.957 92.3603L103.25 91.6531L83.596 71.9994L103.25 52.3457ZM143 72C143 111.212 111.212 143 72 143C32.7878 143 1 111.212 1 72C1 32.7878 32.7878 1 72 1C111.212 1 143 32.7878 143 72Z"
							      stroke="#FF9A5B"
							      strokeWidth="2" />
						</svg>
					</div>

					<div className="flex items-center justify-center py-1 text-brand-green -mt-16 mb-4">
						<LoaderIcon className="animate-rotation w-10 h-10 text-brand-green" />
					</div>
					<div className="mb-4">You are being redirected in {timeLeft} second(s)...</div>
				</div>
			</div>
		);
	}

	if (project) {
		return <Outlet context={{ auth, project }} />
	} else {
		return <Outlet context={{ auth }} />
	}
}
